import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, shareReplay, switchMap } from 'rxjs';
import { DraftEmailActions } from 'src/app/components/dialogs/draft-email/models/draft-email-actions';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { FolderDraftEmailsChangeEvent } from 'src/app/models/events/folder-draft-emails-change-event';
import { MenuType } from 'src/app/models/events/folder-menu-refresh-event';
import { DraftEmail } from 'src/app/models/server/draft-email';
import { CommentsChangeNotification } from 'src/app/models/ws/notifications/comments-change-notification';
import { DocumentAddNotification } from 'src/app/models/ws/notifications/document-add-notification';
import { DocumentDeleteNotification } from 'src/app/models/ws/notifications/document-delete-notification';
import { DocumentUpdateNotification } from 'src/app/models/ws/notifications/document-update-notification';
import { FolderAddNotification } from 'src/app/models/ws/notifications/folder-add-notification';
import { FolderAttributeModificationNotification } from 'src/app/models/ws/notifications/folder-attribute-modification-notification';
import { FolderCloneNotification } from 'src/app/models/ws/notifications/folder-clone-notification';
import { FolderDeleteNotification } from 'src/app/models/ws/notifications/folder-delete-notification';
import { FolderHistoryChangeNotification } from 'src/app/models/ws/notifications/folder-history-change-notification';
import { FolderIdentification } from 'src/app/models/ws/notifications/folder-identification';
import { FolderLockNotification } from 'src/app/models/ws/notifications/folder-lock-notification';
import { FolderMergeNotification } from 'src/app/models/ws/notifications/folder-merge-notification';
import { FolderMoveToBoxNotification } from 'src/app/models/ws/notifications/folder-move-to-box-notification';
import { FolderReadStatusToggleNotification } from 'src/app/models/ws/notifications/folder-read-status-toggle-notification';
import { FolderStandByStateToggleNotification } from 'src/app/models/ws/notifications/folder-stand-by-state-toggle-notification';
import { FolderUnlockRequestNotification } from 'src/app/models/ws/notifications/folder-unlock-request-notification';
import { FolderUnlockResponseNotification } from 'src/app/models/ws/notifications/folder-unlock-response-notification';
import { FolderWaitingArchivingStateToggleNotification } from 'src/app/models/ws/notifications/folder-waiting-archiving-state-toggle-notification';
import { ForceDisconnectionNotification } from 'src/app/models/ws/notifications/force-disconnection-notification';
import { WorkflowDraftEmailNotification } from 'src/app/models/ws/notifications/workflow-draft-email-notification';
import { WorkflowProcessingErrorNotification } from 'src/app/models/ws/notifications/workflow-processing-error-notification';
import { WorkflowProcessingNotification } from 'src/app/models/ws/notifications/workflow-processing-notification';
import { WorkflowStepActivationNotification } from 'src/app/models/ws/notifications/workflow-step-activation-notification';
import { WorkflowStepCompletionNotification } from 'src/app/models/ws/notifications/workflow-step-completion-notification';
import { WorkflowStepStateModificationNotification } from 'src/app/models/ws/notifications/workflow-step-state-modification-notification';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { DialogService } from '../actions/dialog.service';
import { WorkflowNotificationService } from '../actions/workflow-notification.service';
import { DraftEmailsService } from '../http/draft-emails.service';
import { FolderDocumentsService } from '../http/folder-documents.service';
import { FoldersService } from '../http/folders.service';
import { AttributeDefinitionsStateService } from '../state/attribute-definitions-state.service';
import { BoxesStateService } from '../state/boxes-state.service';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { FolderDraftEmailsStateService } from '../state/folder-draft-emails-state.service';
import { TChatStateService } from '../state/tchat-state.service';
import { ViewsStateService } from '../state/views-state.service';
import { WebSocketNotificationService } from './websocket-notification.service';
import { WebSocketTchatService } from './websocket-tchat.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationHandlerService {
    /**
     * Constructeur
     */
    constructor(
        private foldersService: FoldersService,
        private dialogService: DialogService,
        private connectedUserStateService: ConnectedUsersStateService,
        private workflowNotificationService: WorkflowNotificationService,
        private viewsStateService: ViewsStateService,
        private folderDocumentsService: FolderDocumentsService,
        private attributeDefinitionsStateService: AttributeDefinitionsStateService,
        private eventsManagerService: EventsManagerService,
        private webSocketTchatService: WebSocketTchatService,
        private tchatService: TChatStateService,
        private boxesStateService: BoxesStateService,
        private draftEmailsService: DraftEmailsService,
        private folderDraftEmailsStateService: FolderDraftEmailsStateService,
        private router: Router
    ) {}

    public handleDocumentAddNotification(notification: DocumentAddNotification) {
        let listDocumentsQuery = this.folderDocumentsService.list(notification.viewId, notification.folderId).pipe(shareReplay(1));

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            listDocumentsQuery.subscribe((documents) => {
                this.eventsManagerService.emitDocumentsSectionUpdatedEvent(notification.folderId, documents);
                this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, notification.folderId);
            });
        }

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            listDocumentsQuery.subscribe((documents) => {
                this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, notification.folderId);
                this.eventsManagerService.emitFolderDocumentsUpdatedEvent(notification.folderId, documents);
            });
        }

        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleDocumentDeleteNotification(notification: DocumentDeleteNotification) {
        let listDocumentsQuery = this.folderDocumentsService.list(notification.viewId, notification.folderId).pipe(shareReplay(1));

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            listDocumentsQuery.subscribe((documents) => {
                this.eventsManagerService.emitDocumentsSectionUpdatedEvent(notification.folderId, documents, null, true);
                this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, notification.folderId);
                this.eventsManagerService.emitFavoriteSectionUpdatedEvent(notification.viewId, notification.folderId);
            });
        }

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            listDocumentsQuery.subscribe((documents) => {
                this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, notification.folderId);
                this.eventsManagerService.emitFolderDocumentsUpdatedEvent(notification.folderId, documents, null, true);
            });
        }

        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleDocumentUpdateNotification(notification: DocumentUpdateNotification) {
        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            this.folderDocumentsService.list(notification.viewId, notification.folderId).subscribe((documents) => {
                this.eventsManagerService.emitDocumentsSectionUpdatedEvent(notification.folderId, documents);
                this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, notification.folderId);
            });
        }
    }

    public handleFolderAddNotification(notification: FolderAddNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitFollowedFoldersRefreshEvent();

        if (notification.folderIdentification.boxId) {
            this.eventsManagerService.emitBoxCounterUpdatedEvent(notification.folderIdentification.boxId, 1);
        } else {
            this.eventsManagerService.emitViewCounterUpdatedEvent(notification.folderIdentification.viewId, 1);
        }
    }

    public handleFolderAttributeModificationNotification(notification: FolderAttributeModificationNotification) {
        // TODO : Ne faire qu'un update de dossiers si aucun filtre ou tri n'existe sur l'attribut modifié
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitFollowedFoldersRefreshEvent();

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            this.foldersService.get(notification.viewId, notification.folderId).subscribe((folder) => {
                this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, folder);
                this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, folder.id);
                this.eventsManagerService.emitFavoriteSectionUpdatedEvent(notification.viewId, folder);
            });
        }

        // Si l'attribut correspond à BoxId --> decrementer la bannette dans laquelle se trouvait le dossier, incrémenter la nouvelle bannette
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            const boxIdAttributeDefinition = this.attributeDefinitionsStateService
                .getAttributeDefinitions(this.viewsStateService.getSelectedView().id)
                .find((x) => x.propertyCode === FolderPropertyCodes.BOX_ID);
            if (boxIdAttributeDefinition && notification.attributeId === boxIdAttributeDefinition.id) {
                this.eventsManagerService.emitBoxCounterUpdatedEvent(notification.attributeOldValue, -1);
                this.eventsManagerService.emitBoxCounterUpdatedEvent(notification.attributeNewValue, +1);
            }
        }

        // Si l'attribut correspond à Name --> Renommage de l'onglet du dossier s'il est ouvert
        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            const nameAttributeDefinition = this.attributeDefinitionsStateService
                .getAttributeDefinitions(notification.viewId)
                .find((x) => x.propertyCode === FolderPropertyCodes.NAME);
            if (notification.attributeId === nameAttributeDefinition.id) {
                this.eventsManagerService.emitFolderRenamedEvent(notification.folderId, notification.attributeNewValue);
            }
        }
    }

    public handleFolderCloneNotification(notification: FolderCloneNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitFollowedFoldersRefreshEvent();

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.folderSourceId);

        if (notification.folderSourceBoxId) {
            this.eventsManagerService.emitBoxCounterUpdatedEvent(notification.folderSourceBoxId, notification.cloneCount);
        } else {
            this.eventsManagerService.emitViewCounterUpdatedEvent(notification.viewId, notification.cloneCount);
        }
    }

    public handleFolderDeleteNotification(notification: FolderDeleteNotification) {
        // Suppression des dossiers de la liste
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            notification.folderIdentifications.forEach((identification) => {
                this.eventsManagerService.emitListFolderDeletedEvent(identification.folderId);
            });
        }

        notification.folderIdentifications.forEach((identification) => {
            this.eventsManagerService.emitFollowedFoldersDeletedEvent(identification.folderId);
        });

        // Fermeture des dossiers ouverts.
        notification.folderIdentifications.forEach((identification) => {
            this.eventsManagerService.emitFolderTabCloseRequestEvent(identification.folderId, true);
        });

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.updateBoxCounters(notification.folderIdentifications, false);

            this.updateWorkflowCounters(notification.folderIdentifications, false);
        }
    }

    public handleFolderHistoryChangeNotification(notification: FolderHistoryChangeNotification) {
        //console.warn(`Réception d'une notification FolderHistoryChangeNotification`, notification);

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleFolderLockNotification(notification: FolderLockNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            notification.folderIds.forEach((folderId) => {
                this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, folderId);
            });
        }

        notification.folderIds.forEach((folderId) => {
            this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, folderId);
        });
    }

    public handleFolderMergeNotification(notification: FolderMergeNotification) {
        notification.mergedFolderIdentifications.forEach((folderIdentification) => {
            if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
                this.eventsManagerService.emitListFolderDeletedEvent(folderIdentification.folderId);
            }

            this.eventsManagerService.emitFollowedFoldersDeletedEvent(folderIdentification.folderId);

            this.eventsManagerService.emitFolderTabCloseRequestEvent(folderIdentification.folderId, false);
        });

        this.folderDocumentsService.list(notification.viewId, notification.targetFolderId).subscribe((documents) => {
            this.eventsManagerService.emitDocumentsUpdatedEvent(notification.targetFolderId, documents);
        });

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.updateBoxCounters(notification.mergedFolderIdentifications, false);

            this.updateWorkflowCounters(notification.mergedFolderIdentifications, false);
        }

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.targetFolderId);
    }

    public handleFolderMoveToBoxNotification(notification: FolderMoveToBoxNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitFollowedFoldersRefreshEvent();

        notification.movedFolderIdentifications.forEach((folderIdentification) => {
            this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, folderIdentification.folderId);
            this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, folderIdentification.folderId);
        });

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.updateBoxCounters(notification.movedFolderIdentifications, false);
            this.eventsManagerService.emitBoxCounterUpdatedEvent(notification.targetBoxId, notification.movedFolderIdentifications.length);
        }
    }

    public handleFolderReadStatusToggleNotification(notification: FolderReadStatusToggleNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            // Cas où la notification correspond à la vue courante
            notification.folderIds.forEach((folderId) => {
                this.foldersService.get(notification.viewId, folderId).subscribe((folder) => {
                    this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, folder);

                    if (this.eventsManagerService.IsOpenedByCurrentUser(folderId)) {
                        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, folderId);
                        this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, folder);
                    }
                });
            });
        } else {
            // Cas où la notification ne correspond pas à la vue courante
            notification.folderIds.forEach((folderId) => {
                if (this.eventsManagerService.IsOpenedByCurrentUser(folderId)) {
                    this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, folderId);

                    this.foldersService.get(notification.viewId, folderId).subscribe((folder) => {
                        this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, folder);
                    });
                }
            });
        }

        notification.folderIds.forEach((folderId) => {
            this.foldersService.get(notification.viewId, folderId).subscribe((folder) => {
                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, folder);

                if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
                    this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, folder);
                }
                this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, folderId);
                this.eventsManagerService.emitAttributesSectionUpdatedEvent(notification.viewId, folder);
            });
        });
    }

    public handleFolderStandByStateToggleNotification(notification: FolderStandByStateToggleNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
                // Mise à jour des dossiers
                notification.folderIdentifications.forEach((identification) => {
                    this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, identification.folderId);
                });
            } else {
                // Ajout ou suppression des dossiers
                this.eventsManagerService.emitListRefreshEvent();

                const isIncrement = notification.isInStandByMode ? false : true;

                this.updateBoxCounters(notification.folderIdentifications, isIncrement);

                this.updateWorkflowCounters(notification.folderIdentifications, isIncrement);
            }
        }

        if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
            // Mise à jour des dossiers
            notification.folderIdentifications.forEach((identification) => {
                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, identification.folderId);
            });
        } else {
            // Ajout ou suppression des dossiers
            this.eventsManagerService.emitFollowedFoldersRefreshEvent();
        }

        notification.folderIdentifications.forEach((identification) => {
            this.eventsManagerService.emitFolderMenuRefreshEvent(identification.folderId, MenuType.StandBy, notification.isInStandByMode);
        });
    }

    public handleFolderWaitingArchivingStateToggleNotification(notification: FolderWaitingArchivingStateToggleNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            if (this.connectedUserStateService.getUserConnected().params.isFolderInArchivingModeVisible) {
                // Mise à jour des dossiers
                notification.folderIdentifications.forEach((identification) => {
                    this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, identification.folderId);
                });
            } else {
                // Ajout ou suppression des dossiers
                this.eventsManagerService.emitListRefreshEvent();

                const isIncrement = notification.IsWaitingArchiving ? false : true;

                this.updateBoxCounters(notification.folderIdentifications, isIncrement);

                this.updateWorkflowCounters(notification.folderIdentifications, isIncrement);
            }
        }

        if (this.connectedUserStateService.getUserConnected().params.isFolderInArchivingModeVisible) {
            // Mise à jour des dossiers
            notification.folderIdentifications.forEach((identification) => {
                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, identification.folderId);
            });
        } else {
            // Ajout ou suppression des dossier
            this.eventsManagerService.emitFollowedFoldersRefreshEvent();
        }

        notification.folderIdentifications.forEach((identification) => {
            this.eventsManagerService.emitFolderMenuRefreshEvent(identification.folderId, MenuType.WaitingArchiving, notification.IsWaitingArchiving);
        });
    }

    public handleCommentsChangeNotification(notification: CommentsChangeNotification) {
        //console.warn(`Réception d'une notification CommentsChangeNotification`, notification);

        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            // TODO : Emettre un evenement pour raffraichir le détail déplié d'un dossier depuis la liste des dossiers
        }

        this.eventsManagerService.emitCommentChangeEvent2(notification.viewId, notification.folderId);
    }

    public handleFolderUnlockRequestNotification(notification: FolderUnlockRequestNotification) {
        this.foldersService
            .get(notification.viewId, notification.folderId)
            .pipe(
                switchMap(() => {
                    // Ouvre une fenêtre de demande de vérrouillage à l'utilisateur concerné
                    return this.dialogService.openUnlockFolderRequest(notification.userName, notification.folderName, 30).pipe(
                        switchMap((response: Boolean) => {
                            if (response === true) {
                                // S'il accepte, envoi de la réponse
                                return this.foldersService.acceptRequestUnlockFolder(notification.viewId, notification.folderId, notification.userLogin).pipe(
                                    map(() => {
                                        // Fermeture du dossier vérrouillé
                                        this.eventsManagerService.emitFolderTabCloseRequestEvent(notification.folderId, false);
                                    })
                                );
                            } else {
                                // S'il refuse, envoi de la réponse
                                return this.foldersService.declineRequestUnlockFolder(notification.viewId, notification.folderId, notification.userLogin);
                            }
                        })
                    );
                })
            )
            .subscribe();
    }

    public handleFolderUnlockResponseNotification(notification: FolderUnlockResponseNotification) {
        this.dialogService.openUnlockFolderResponse(notification.userName, notification.folderName, notification.accept).subscribe();
    }

    public handleWorkflowProcessingErrorNotification(notification: WorkflowProcessingErrorNotification) {
        console.warn(`Le workflow a généré une erreur '${notification.code}' - '${notification.error}'`);
        this.workflowNotificationService.closeToast(notification.folderId);
        this.workflowNotificationService.displayError(notification.folderId, 'Workflow', `Le workflow a généré une erreur.`, false);
    }

    public handleWorkflowProcessingNotification(notification: WorkflowProcessingNotification) {
        if (notification.done) {
            this.workflowNotificationService.closeToast(notification.id);
            this.workflowNotificationService.displaySuccess(notification.id, 'Workflow', `L'exécution du workflow du dossier est terminée...`, false);
        } else {
            this.workflowNotificationService.displayInfo(notification.id, 'Workflow', `Le workflow du dossier est en cours d\'exécution...`, true);
        }
    }

    public handleWorkflowStepActivationNotification(notification: WorkflowStepActivationNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitFollowedFoldersRefreshEvent();

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            this.foldersService.getFolderWorkflowStepSnapshots(this.viewsStateService.getSelectedView().id, notification.folderId).subscribe((stepSnapshots) => {
                this.eventsManagerService.emitWorkflowSectionUpdatedeEvent(notification.folderId, stepSnapshots);
            });
        }

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleWorkflowStepCompletionNotification(notification: WorkflowStepCompletionNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListRefreshEvent();
        }

        this.eventsManagerService.emitListRefreshEvent();

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            this.foldersService.getFolderWorkflowStepSnapshots(this.viewsStateService.getSelectedView().id, notification.folderId).subscribe((stepSnapshots) => {
                this.eventsManagerService.emitWorkflowSectionUpdatedeEvent(notification.folderId, stepSnapshots);
            });
        }

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleWorkflowStepStateModificationNotification(notification: WorkflowStepStateModificationNotification) {
        if (this.viewsStateService.getSelectedView() && notification.viewId == this.viewsStateService.getSelectedView().id) {
            this.eventsManagerService.emitListFolderUpdatedEvent(notification.viewId, notification.folderId);
        }

        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(notification.viewId, notification.folderId);

        if (this.eventsManagerService.IsOpenedByCurrentUser(notification.folderId)) {
            this.foldersService.getFolderWorkflowStepSnapshots(this.viewsStateService.getSelectedView().id, notification.folderId).subscribe((stepSnapshots) => {
                this.eventsManagerService.emitWorkflowSectionUpdatedeEvent(notification.folderId, stepSnapshots);
            });
        }

        this.eventsManagerService.emitHistorySectionUpdatedEvent(notification.viewId, notification.folderId);
    }

    public handleForceDisconnectionNotification(notification: ForceDisconnectionNotification, webSocketNotificationService: WebSocketNotificationService) {
        this.forceLogoff(webSocketNotificationService);
        this.router.navigate(['/login']);
    }

    public handleWorkflowDraftEmailNotification(notification: WorkflowDraftEmailNotification) {
        this.draftEmailsService
            .get(notification.viewId, notification.folderId, notification.draftEmailId)
            .pipe(
                switchMap((draftEmail: DraftEmail) => {
                    return this.dialogService.openAutoGeneratedEmail(draftEmail);
                })
            )
            .subscribe((x) => {
                if (x.action === DraftEmailActions.Deleted) {
                    // Suppression du brouillon de mail
                    this.draftEmailsService.delete(notification.viewId, x.draftEmail).subscribe();
                } else if (x.action === DraftEmailActions.Sended) {
                    // Ne fait rien
                } else if (x.action === DraftEmailActions.Saved) {
                    // Sauvegarde du mail --> le brouillon de mail est conservé.
                    this.folderDraftEmailsStateService.updateFolderDraftEmailChange(new FolderDraftEmailsChangeEvent(x.draftEmail.folderId, true));
                } else if (x.action === DraftEmailActions.Saved) {
                    // Annulation de l'envoi de mail --> le brouillon de mail est conservé.
                    this.folderDraftEmailsStateService.updateFolderDraftEmailChange(new FolderDraftEmailsChangeEvent(x.draftEmail.folderId, true));
                }
            });
    }

    private updateWorkflowCounters(folderIdentifications: FolderIdentification[], isIncrement: boolean) {
        const groupedByView = folderIdentifications.reduce(
            (
                group: {
                    [key: number]: FolderIdentification[];
                },
                item
            ) => {
                if (item.viewId) {
                    if (!group[item.viewId]) {
                        group[item.viewId] = [];
                    }
                    group[item.viewId].push(item);
                }
                return group;
            },
            {}
        );

        for (const viewId in groupedByView) {
            if (groupedByView.hasOwnProperty(viewId)) {
                const group = groupedByView[Number(viewId)];
                const increment = isIncrement ? group.length : group.length * -1;
                this.eventsManagerService.emitViewCounterUpdatedEvent(Number(viewId), increment);
            }
        }
    }

    private updateBoxCounters(folderIdentifications: FolderIdentification[], isIncrement: boolean) {
        const groupedByBox = folderIdentifications.reduce(
            (
                group: {
                    [key: string]: FolderIdentification[];
                },
                item
            ) => {
                if (item.boxId) {
                    if (!group[item.boxId]) {
                        group[item.boxId] = [];
                    }
                    group[item.boxId].push(item);
                }
                return group;
            },
            {}
        );

        for (const boxId in groupedByBox) {
            if (groupedByBox.hasOwnProperty(boxId)) {
                const group = groupedByBox[boxId];
                const increment = isIncrement ? group.length : group.length * -1;
                this.eventsManagerService.emitBoxCounterUpdatedEvent(boxId, increment);
            }
        }
    }

    // Force la Deconnexion de l'utilisateur
    private forceLogoff(webSocketNotificationService: WebSocketNotificationService) {
        webSocketNotificationService.stopSocket(this.connectedUserStateService.getUserConnected().login);
        this.webSocketTchatService.stopSocket(this.connectedUserStateService.getUserConnected().login);
        this.tchatService.roomSelectedChange(null);
        this.connectedUserStateService.setUserConnected(null);
        this.boxesStateService.setBoxesGroupsList([]);
        this.viewsStateService.setSelectedView(null);
    }
}
